import { User } from "users/user";
import { Masked } from "work/_shared/masked-fields";
import { ProposalAction } from "work/proposals/__constants/constants";
import Proposal from "work/proposals/proposal";

User.prototype.canShare = function (proposal: Proposal | null | undefined): boolean {
  if (!proposal) return false;
  if (this.canEdit(proposal)) return true;
  if (this.canRedline(proposal)) return true;
  if (
    proposal.clientReviewers
      .concat(proposal.vendorReviewers)
      .some((reviewer) => reviewer.userId.isEqualTo(this.id) && reviewer.canEdit)
  )
    return true;
  return false;
};

User.prototype.canEdit = function (proposal: Proposal | null | undefined): boolean {
  if (!proposal) return false;
  return proposal.availableActions[ProposalAction.Edit].some((id) => id.isEqualTo(this.id));
};

User.prototype.canRevise = function (proposal: Proposal | null | undefined): boolean {
  if (!proposal) return false;
  return proposal.availableActions[ProposalAction.Revise].some((id) => id.isEqualTo(this.id));
};

User.prototype.canRedline = function (proposal: Proposal | null | undefined): boolean {
  if (!proposal) return false;
  if (this.canRevise(proposal)) return true;

  return (
    proposal.clientReviewers.some((r) => r.canEdit && r.userId.isEqualTo(this.id)) ||
    proposal.vendorReviewers.some((r) => r.canEdit && r.userId.isEqualTo(this.id))
  );
};

User.prototype.canApprove = function (proposal: Proposal | null | undefined): boolean {
  if (!proposal) return false;
  if (!this?.id) return false;
  if (
    Masked.isMasked(proposal.workAgreement.conflictsCheckWaived) ||
    Masked.isMasked(proposal.workAgreement.conflicts) ||
    (!proposal.workAgreement.conflictsCheckWaived.value && proposal.workAgreement.conflicts.length === 0)
  ) {
    return false;
  }

  return proposal.availableActions[ProposalAction.Approve].some((id) => id.isEqualTo(this.id));
};

User.prototype.canReject = function (proposal: Proposal | null | undefined): boolean {
  if (!proposal) return false;
  return proposal.availableActions[ProposalAction.Reject].some((id) => id.isEqualTo(this.id));
};

User.prototype.isClient = function (proposal: Proposal | null | undefined): boolean {
  if (!proposal) return false;
  if (Masked.isMasked(proposal.clientLeader) || Masked.isMasked(proposal.vendorLeader)) return false;
  return proposal.clientLeader?.userId.isEqualTo(this.id) || false;
};

User.prototype.isAssociatedWithClient = function (proposal: Proposal | null | undefined): boolean {
  if (!proposal) return false;
  if (this.isClient(proposal)) return true;
  if (proposal.clientReviewers.some((reviewer) => reviewer.userId.isEqualTo(this.id))) return true;
  return false;
};

User.prototype.isVendor = function (proposal: Proposal | null | undefined): boolean {
  if (!proposal) return false;
  if (Masked.isMasked(proposal.vendorLeader) || Masked.isMasked(proposal.clientLeader)) return false;
  return proposal.vendorLeader?.userId.isEqualTo(this.id) || false;
};

User.prototype.isAssociatedWithVendor = function (proposal: Proposal | null | undefined): boolean {
  if (!proposal) return false;
  if (this.isVendor(proposal)) return true;
  if (proposal.additionalVendors?.some((vendor) => vendor.userId.isEqualTo(this.id))) return true;
  if (proposal.vendorReviewers.some((reviewer) => reviewer.userId.isEqualTo(this.id))) return true;
  return false;
};

User.prototype.canHire = function (proposal: Proposal | null | undefined): boolean {
  if (!proposal) return false;
  return proposal.availableActions[ProposalAction.Hire].some((id) => id.isEqualTo(this.id));
};
